/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import {
  ICodeableConcept,
  ICoding,
  IIdentifier,
  IObservation,
  IPatient,
  IReference,
} from '@ahryman40k/ts-fhir-types/lib/R4/Resource';

import {
  DocumentVersionType,
  FhirWidgetBase,
  FhirPredefinedWidget,
  FhirOurObservations,
  FhirRuleType,
  FhirPageWidgetType,
  PageFhirType,
  PageOptionExclusiveType,
  PageOptionMultipleChoiceType,
  PageType,
  PageTypeEnum,
} from '../algorithm/types';
import { bestEffortFilter, formatDateTime, sortByUTCDate } from '../utils';
import { FhirPatientDataType } from './fhirSlice';
import { getPearlCodeForObservation } from './useFhirHooks';
import {
  IDiagnosticReportBundleSimplified,
  IDiagnosticReportSimplified,
  IObservationSimplified,
  IObservationWithPearlCode,
  ObservationValueInterpretationEnum,
} from './fhir_types';
import { simpleFilter } from './fuzzy';
import { getCodeableConceptText } from './codeableConcept';
import { enableMuscCPTCodes } from '../permissions';
import { CareEverywherePanel } from './CareEverywhere/network';

export function flatMapDiagnosticReportsToObservations(diagnosticReports: IDiagnosticReportSimplified[] | undefined) {
  return diagnosticReports
    ?.flatMap((report) => report.result)
    .filter((x) => x !== undefined) as IObservationSimplified[];
}

const observationNameSeparatorsPattern = /[^a-zA-Z0-9%]+/g; // If you change, update in tools/musc/common.ts and below as well
export const fhirRuleDisallowedCharacterPattern = /[^a-zA-Z0-9%| ]/g;

function isCptCoding(coding: ICoding) {
  return coding.system === 'http://www.ama-assn.org/go/cpt' || coding.system === 'urn:oid:2.16.840.1.113883.6.12';
}

/** Replace punctuation with spaces so words do not become accidentally joined. If you change, update tools/musc/common.ts. */
export function simplifyObservationName(name: string) {
  return name.replace(observationNameSeparatorsPattern, ' ').replace(/\s+/g, ' ').trim();
}

export function expandLegacyFhirRuleRange(value: string): { values?: string[]; error?: string } {
  let values = [value];
  let match = value.match(/\[([^\]]*)\]/);
  while (match) {
    if (!/^\d-\d$/.test(match[1]!)) {
      return { error: `Only ascending single-digit ranges like [5-6] are allowed; found ${match[0]}` };
    }
    const start = Number(match[1]![0]);
    const end = Number(match[1]![2]);
    if (start >= end) return { error: `Range ${match[0]} must have a lower start digit than end digit` };
    values = values.flatMap((current) => {
      const currentMatch = current.match(/\[\d-\d\]/);
      if (!currentMatch) return [current];
      return Array.from({ length: end - start + 1 }, (_, index) =>
        current.replace(currentMatch[0], String(start + index))
      );
    });
    match = values[0]?.match(/\[([^\]]*)\]/) ?? null;
  }
  if (values.some((entry) => /[[\]]/.test(entry))) {
    return { error: 'Only ascending single-digit ranges like [5-6] are allowed' };
  }
  return { values };
}

export function parseFhirRuleValues(value: string | undefined): string[] {
  const normalizedValues = value
    ?.split('|')
    .flatMap((entry) => expandLegacyFhirRuleRange(entry).values ?? [entry])
    .map((entry) => (entry === '.*' ? entry : simplifyObservationName(entry)))
    .filter(Boolean);
  return [...new Set(normalizedValues)];
}

function getLegacyRangeError(value: string | undefined): string | undefined {
  for (const entry of value?.split('|') ?? []) {
    const error = expandLegacyFhirRuleRange(entry).error;
    if (error) return error;
  }
  return undefined;
}

export function getFhirRuleConfigurationError(rules: FhirRuleType | undefined): string | undefined {
  if (!rules) return undefined;
  const fields = [
    ['CPT / Diagnostic Report name', rules.name],
    ['MUSC CPT / Diagnostic Report name', rules.muscName],
    ['Hide All If No Whitelist Matches', rules.hideAllIfNoWhitelistMatches],
    ['Loinc Codes', rules.loincCodes],
  ] as const;
  for (const [label, value] of fields) {
    const error = getLegacyRangeError(value);
    if (error) return `${label}: ${error}`;
  }
  return undefined;
}

export const DIAGNOSTIC_REPORT_CONCLUSION_NAME = 'Impression';

/** FHIR R4 allows a DiagnosticReport with no `result` that only has a `conclusion` and/or `conclusionCode`
 * (common for imaging). Widgets render observations, so present the conclusion as an "Impression" observation,
 * which is also the name imaging widgets whitelist.
 */
export function getDiagnosticReportResults(diagnosticReport: IDiagnosticReportSimplified): IReference[] {
  if (diagnosticReport.result?.length) return diagnosticReport.result;
  const conclusion =
    diagnosticReport.conclusion?.trim() ||
    diagnosticReport.conclusionCode
      ?.map((code) => getCodeableConceptText(code))
      .filter(Boolean)
      .join('; ');
  if (!conclusion) return [];
  const conclusionObservation: IObservationSimplified = {
    resourceType: 'Observation',
    id: `${diagnosticReport.id ?? 'diagnostic-report'}-conclusion`,
    status: 'final' as IObservationSimplified['status'],
    code: { text: DIAGNOSTIC_REPORT_CONCLUSION_NAME },
    effectiveDateTime: diagnosticReport.effectiveDateTime,
    valueString: conclusion,
  };
  return [conclusionObservation as IReference];
}

export function applyRulesToFhirPatientData(
  fhirPatientData: FhirPatientDataType,
  careEverywhereData: CareEverywherePanel[] | undefined,
  rules: FhirRuleType,
  filter?: string,
  groupedObservationIdsByCptCode?: Record<string, Set<string>> | undefined
): DiagnosticReportAndObservationsBase[] | undefined {
  if (!fhirPatientData && !careEverywhereData) return undefined;
  const ruleError = getFhirRuleConfigurationError(rules);
  if (ruleError) return [];
  // We need to combine the fhirPatientData and careEverywhereData into one array
  const combinedFhirPatientDataWithCE: IDiagnosticReportSimplified[] = [
    ...(fhirPatientData || []),
    ...((careEverywhereData as IDiagnosticReportSimplified[]) || []),
  ];

  // Originally we supported multiples rules.  The idea was that if the first rule doesn't match,
  // use the next rule, and so on.  But this was removed so now you can only
  // create one rule.
  const rule = rules;
  const hideAllIfNoWhitelistMatchesValues = parseFhirRuleValues(rule.hideAllIfNoWhitelistMatches);
  const observations = combinedFhirPatientDataWithCE
    .filter((diagnosticReport) => doesDiagnosticReportMatchFhirWidget(rule, diagnosticReport))
    .map((diagnosticReport) => ({ diagnosticReport, results: getDiagnosticReportResults(diagnosticReport) }))
    .filter(({ results }) => results.length > 0)
    .map(({ diagnosticReport, results }) => {
      const observationList = results
        .filter((x) => (x as any).resourceType !== 'OperationOutcome')
        .filter(
          (x) =>
            !filter ||
            simpleFilter(getCodeableConceptText((x as IObservation).code), filter) ||
            simpleFilter((x as IObservation).valueString, filter) ||
            // check the diagnostic report itself too
            simpleFilter(diagnosticReport.code?.text, filter)
        )
        .map((obs) => {
          const pearlCode = getPearlCodeForObservation((obs as IObservationSimplified).code);
          return {
            ...obs,
            pearlCode,
            observationGroupId: groupedObservationIdsByCptCode?.[pearlCode]?.entries().next().value?.[0],
          };
        }) as IObservationWithPearlCode[];
      const isLoaded = !(observationList?.[0] as IReference)?.reference;

      const hideAllIfNoWhitelistMatches =
        hideAllIfNoWhitelistMatchesValues.length > 0 &&
        diagnosticReportCodeMatchesValues(
          diagnosticReport.code,
          hideAllIfNoWhitelistMatchesValues,
          enableMuscCPTCodes(),
          []
        );

      return {
        diagnosticReport,
        observationList,
        isLoaded,
        hideAllIfNoWhitelistMatches: !!hideAllIfNoWhitelistMatches,
      };
    });
  const observationsFiltered = observations.filter((x) => !!x.observationList && x.observationList.length > 0);
  // If this rule has any match observations, return them, otherwise go on to the next rule.
  if (observationsFiltered.length > 0) return observationsFiltered;

  return [];
}

/** Take a rule like:
 *
 *  "12345|67890|Comprehensive Metabolic Panel" and see if it matches the diagnostic panel name or CPT code.
 *
 * If we're on MUSC, then also look at the muscNames.
 * Also check the Loinc codes if they are present, but only if there's no CPT code.
 */
export function doesDiagnosticReportMatchFhirWidget(
  rule: FhirRuleType,
  diagnosticReport: IDiagnosticReportSimplified
): boolean {
  if (getFhirRuleConfigurationError(rule)) return false;
  const nameValues = parseFhirRuleValues(rule.name);
  const musc = enableMuscCPTCodes() ? rule.muscName?.trim() : undefined;
  const combinedNameValues = [...nameValues, ...parseFhirRuleValues(musc)];
  const loincValues = parseFhirRuleValues(rule.loincCodes);
  if (combinedNameValues.length || loincValues.length) {
    // Text is something like: 'Comprehensive Metabolic Panel' or it is a CPT code itself
    // or if there's no CPT code, try to match by loinc code
    if (diagnosticReportCodeMatchesValues(diagnosticReport?.code, combinedNameValues, !!musc, loincValues)) {
      return true;
    }
  }
  return false;
}

/** Redlist diagnostic report names that are too generic.
 * "Historical Imaging Result" and "External Scan" for example are names that have been used in different
 * unrelated diagnostic reports. So matches on these names are not useful.
 *
 * https://pearl-project.atlassian.net/browse/PRL-12809
 */
const REDLIST_DIAGNOSTIC_REPORT_NAME_REGEX =
  /^(?:historical|external|outside|edie|care ?everywhere|diagnostic report -? ?external scan|exception alert|ed information exchange)(?: (?:imaging|lab(?:s|oratory)?|result|results|report|reports|test|tests|study|studies))?$|^historical imaging result|^external scan|^labs external scan|^edie (?:alert|notification|summary|report)|misc referral$/i;

/** Check normalized literal values against the diagnostic report code, text, and display.
 *
 * Note: We don't use the diagnostic report redlist here, because although it may seem like a good idea to filter out these generic names,
 * it could also inadvertently exclude valid matches. So we keep them, but then in the GUI we should show a warning to the user that it might not be
 * relevant.
 * @see REDLIST_DIAGNOSTIC_REPORT_NAME_REGEX
 */
function diagnosticReportCodeMatchesValues(
  code: ICodeableConcept | undefined,
  values: string[],
  testAgainstMuscInternal: boolean,
  loincValues: string[]
): boolean {
  const normalizedValues = new Set(values.map((value) => value.toLowerCase()));
  const normalizedLoincValues = new Set(loincValues.map((value) => value.toLowerCase()));
  function valuesInclude(text: string | undefined, candidates = normalizedValues): boolean {
    return candidates.has('.*') || (!!text && candidates.has(simplifyObservationName(text).toLowerCase()));
  }

  if (valuesInclude(code?.text)) return true;

  function checkIfCodingMatches(coding: ICoding) {
    // Lonic code is http://loinc.org
    // Musc internal code is urn:oid:1.2.840.114350.1.13.307.2.7.5.737384.324
    const isLoinc =
      coding.system === 'http://loinc.org' || // Lonic code
      coding.system === 'urn:oid:2.16.840.1.113883.6.1';
    if (
      isCptCoding(coding) ||
      isLoinc ||
      (testAgainstMuscInternal && coding.system === 'urn:oid:1.2.840.114350.1.13.307.2.7.5.737384.324')
    ) {
      if (valuesInclude(coding.code) || (!isLoinc && valuesInclude(coding.code?.substring(0, 5)))) return true;
      if (isLoinc && valuesInclude(coding.code, normalizedLoincValues)) return true;
    }
    // Check if the full display text matches, but not the historical imaging result or external scan which
    // we blacklist as being too generic.
    return valuesInclude(coding.display);
  }
  return !!code?.coding?.some(checkIfCodingMatches);
}

export function isRuleEmpty(r: FhirRuleType): boolean {
  return !r.name;
}

export function getPatientMRN(patient: IPatient | undefined, preferredMRN?: string): IIdentifier | undefined {
  /* Athena is so annoying - it has no identifier use and has no mrn and just has a system http://fhir.athena.io/sid/ah-patient which returns an id like:
      a-1959731.E-26782 */
  const mrns = patient?.identifier?.filter(
    (x) =>
      (x.use === 'usual' || x.use === 'official' || x.use === undefined) &&
      (x.type?.text?.includes('MRN') ||
        x.type?.text === 'MR' ||
        x.type?.text === 'PAMC' ||
        x.system === 'https://fhir.athena.io/sid/ah-patient')
  );
  if (preferredMRN) {
    // Prefer the MRN that the user typed in.  it would be very weird if this mrn doesn't exist
    const preferred = mrns?.find((x) => x.value?.toLowerCase() === preferredMRN.toLowerCase());
    if (preferred) {
      return preferred;
    } else {
      console.log(
        `Preferred MRN ${preferredMRN} not found for patient ${
          getPatientName(patient) || patient?.id
        }. Available MRNs: ${mrns?.map((x) => x.value).join(', ')}`
      );
    }
  }
  // Prefer ORCA MRN if possible
  const orcamrn = mrns?.find((x) => x.type?.text === 'ORCA MRN');
  if (orcamrn) return orcamrn;
  return mrns?.[0];
  // TODO - worst case should we use the id?
}

export function getPatientName(patient: IPatient | undefined): string | undefined {
  const name = bestEffortFilter(patient?.name, (x) => x.use === 'official')?.[0];
  if (!name) return undefined;

  // Providence sets name.text but athena only gives up given and family.
  return name.text || [...(name.given || []), name.family].filter((x) => x).join(' ');
}

export function getOurObservationsIndexMap(whitelist: FhirOurObservations[] | undefined) {
  const map: Record<string, number> = {};
  whitelist?.forEach((o, index) => o.name && (map[o.name] = index)); // Add a map for both the name and code
  whitelist?.forEach((o, index) => (map[o.code] = index));
  return map;
}

type FhirHl7TypeType =
  | 'social-history'
  | 'vital-signs'
  | 'imaging'
  | 'laboratory'
  | 'procedure'
  | 'survey'
  | 'exam'
  | 'therapy'
  | 'activity';

export type FhirTypeType = 'Labs' | 'Medication' | 'Procedure' | 'Diagnostics' | 'Referral';

const mapHl7ToFhirType: { [key: string]: FhirTypeType } = {
  laboratory: 'Labs',
  procedure: 'Procedure',
};

export interface DiagnosticReportAndObservationsBase {
  diagnosticReport: IDiagnosticReportSimplified;
  observationList: IObservationWithPearlCode[];
  isLoaded: boolean;
  hideAllIfNoWhitelistMatches: boolean;
  ageMonths?: number;
}

export interface DiagnosticReportAndObservations extends DiagnosticReportAndObservationsBase {
  hiddenObservationList: IObservationWithPearlCode[]; // observations not shown in observationList because of white/black list
  type?: FhirTypeType;
  hidePanelFromDateSelector: boolean;
}

function getFhirType({ diagnosticReport, observationList }: DiagnosticReportAndObservations): FhirTypeType | undefined {
  for (var o of observationList) {
    if (o.category) {
      for (var cat of o.category) {
        if (cat.coding) {
          for (var coding of cat.coding) {
            if (coding.system === 'http://terminology.hl7.org/CodeSystem/observation-category') {
              return mapHl7ToFhirType[coding.code as FhirHl7TypeType];
            }
          }
        }
      }
    }
  }
  return undefined;
}

export function getWhiteListIndex(whitelistIndexMap: Record<string, number>, a: IObservationWithPearlCode) {
  const aIndex = whitelistIndexMap[a.pearlCode];
  return aIndex != null ? aIndex : whitelistIndexMap[getCodeableConceptText(a.code) || ''];
}

/** Takes real observations, and sorts them according to the order in whitelist,  */
export function sortFilterDiagnosticReportObservations(
  panelWithObservations: DiagnosticReportAndObservationsBase[] | undefined,
  whitelistIndexMap?: Record<string, number>, // This is just a cached version of getOurObservationsIndexMap(whitelist)
  whitelist?: FhirOurObservations[],
  blacklistIndexMap?: Record<string, number>, // This is just a cached version of getOurObservationsIndexMap(blacklist)
  blacklist?: FhirOurObservations[],
  filter?: boolean
): DiagnosticReportAndObservations[] | undefined {
  // console.log('panelWithObservations', panelWithObservations);
  const applyWhiteListFilter = filter && whitelist && whitelistIndexMap && whitelist.length > 0;
  const ret = panelWithObservations
    ?.map((o, index) => {
      const hiddenObservationList: IObservationWithPearlCode[] = [];
      let observationList = applyWhiteListFilter
        ? o.observationList.filter((o) => {
            if ((o as IReference).reference) {
              return true;
            }
            const keep = getWhiteListIndex(whitelistIndexMap, o) != null;
            if (!keep) hiddenObservationList.push(o);
            return keep;
          })
        : [...o.observationList];
      if (whitelist && whitelistIndexMap) {
        // Add the whitelist id to the observation
        observationList = observationList.map((o) => {
          const index = getWhiteListIndex(whitelistIndexMap, o);
          if (index != null && whitelist[index]) {
            return { ...o, whitelistId: whitelist[index]!.id };
          }
          return o;
        });
      }
      if (filter && blacklist && blacklistIndexMap && blacklist.length > 0) {
        observationList = observationList.filter((o) => {
          const keep = blacklistIndexMap[o.pearlCode] == null;
          if (!keep) hiddenObservationList.push(o);
          return keep;
        });
      }

      /* Hide the date selector on a fhir widget for dates when we have a whitelist
       *  and we're hiding-all-if-no-whitelist-matches and we haven't loaded the data yet.
       *  This is because we won't know whether a date has data until we load it
       *  and we've filtered out data that we don't want.
       *  It is confusing if we show a date, then when they click it we say "no data".
       */
      const hidePanelFromDateSelector =
        filter &&
        applyWhiteListFilter &&
        o.hideAllIfNoWhitelistMatches &&
        (!o.isLoaded || observationList.length === 0);
      const date =
        o.diagnosticReport.effectiveDateTime ||
        (o.diagnosticReport.result?.[0] as IObservationSimplified | undefined)?.effectiveDateTime;
      return {
        diagnosticReport: o.diagnosticReport,
        isLoaded: o.isLoaded,
        hideAllIfNoWhitelistMatches: !!o.hideAllIfNoWhitelistMatches,
        hidePanelFromDateSelector: !!hidePanelFromDateSelector,
        hiddenObservationList,
        ageMonths: date ? monthDiff(new Date(date)) : undefined,
        observationList: whitelistIndexMap
          ? observationList.sort((a, b) => {
              // Note this logic should match the logic in FhirWidgetPreviews.tsx
              const aIndex = getWhiteListIndex(whitelistIndexMap, a);
              const bIndex = getWhiteListIndex(whitelistIndexMap, b);
              if (aIndex === undefined && bIndex === undefined) return 0;
              if (aIndex === undefined) return 1;
              if (bIndex === undefined) return -1;
              return aIndex - bIndex;
            })
          : observationList,
      };
    })
    .map((o) => ({ ...o, type: getFhirType(o) }));
  return ret;
}

function sortByEffectiveDateTime(list: DiagnosticReportAndObservations[]) {
  return list.sort((a, b) => sortByUTCDate(a.diagnosticReport.effectiveDateTime, b.diagnosticReport.effectiveDateTime));
}

/** Takes the output of sortFilterDiagnosticReportObservations and groups
 *  by code.  So if you have 2 panels with the same code, they will be
 *  grouped together, newest first.
 *
 *  Unless singleGroup is true, in which case it will return all the observations
 *  in a single group.
 */
export function sortFilterDiagnosticReportObservationsGrouped(
  panelWithObservations: DiagnosticReportAndObservationsBase[] | undefined,
  whitelistIndexMap: Record<string, number>,
  whitelist: FhirOurObservations[] | undefined,
  blacklistIndexMap: Record<string, number>,
  blacklist: FhirOurObservations[] | undefined,
  filter?: boolean,
  singleGroup?: boolean
): DiagnosticReportAndObservations[][] | undefined {
  const sorted = sortFilterDiagnosticReportObservations(
    panelWithObservations,
    whitelistIndexMap,
    whitelist,
    blacklistIndexMap,
    blacklist,
    filter
  );
  if (!sorted || sorted.length === 0) return undefined;
  if (singleGroup) return [sortByEffectiveDateTime(sorted)];
  const res: DiagnosticReportAndObservations[][] = [];
  const map: Record<string, DiagnosticReportAndObservations[]> = {};
  sorted.forEach((o) => {
    const key = o.diagnosticReport.code?.text || o.diagnosticReport.code?.coding?.[0]?.code;
    if (!key) {
      // Add directly to res
      res.push([o]);
    } else {
      if (map[key] === undefined) map[key] = [];
      map[key]!.push(o);
    }
  });
  Object.values(map).forEach((v) => res.push(sortByEffectiveDateTime(v)));
  return res;
}

export function getLoincCoding(coding: ICoding[] | undefined) {
  return coding?.find((code) => code.system === 'http://loinc.org' || code.system === 'urn:oid:2.16.840.1.113883.6.1');
}

export function getLoincCode(coding: ICoding[] | undefined) {
  return getLoincCoding(coding)?.code;
}

export function getCpt4Coding(coding: ICoding[] | undefined) {
  return coding?.find(isCptCoding);
}

export function getCpt4Code(coding: ICoding[] | undefined) {
  return getCpt4Coding(coding)?.code;
}

/** Get the DiagnosticReport text to show as a title to the user.  Normally we just use the
 * code.text, but if it is redlisted, we would prefer not to use it and would prefer to use
 * the cpt coding display instead.
 */
export function getDiagnosticReportText(coding: ICodeableConcept | undefined): {
  text: string | undefined;
  isDiagnosticReportTextRedlisted: boolean;
} {
  if (!coding) return { text: undefined, isDiagnosticReportTextRedlisted: false };
  const text = coding.text?.trim();
  if (!text) {
    return { text, isDiagnosticReportTextRedlisted: false };
  }
  // If the text is redlisted, check to see the display of the cpt coding in case that's not redlisted.
  const isDiagnosticReportTextRedlisted = REDLIST_DIAGNOSTIC_REPORT_NAME_REGEX.test(text || '');
  if (isDiagnosticReportTextRedlisted) {
    // If the text is blacklisted, check if we have a CPT coding with a display
    const cptCodingDisplay = getCpt4Coding(coding.coding)?.display;
    if (cptCodingDisplay && !REDLIST_DIAGNOSTIC_REPORT_NAME_REGEX.test(cptCodingDisplay)) {
      // If we have a coding with a display, return that instead
      return { text: cptCodingDisplay, isDiagnosticReportTextRedlisted: false };
    }
    // If we don't have a coding with a display, return the text anyway but set the flag
    return { text, isDiagnosticReportTextRedlisted: true };
  }
  // Otherwise we return the text as is
  return { text, isDiagnosticReportTextRedlisted: false };
}

/** Before 2023/03/14 there was no scope and matching on fhir rule, and instead it was just part of the .name string.
 *  Then this function was changed to parsing a name like 'o:.*Hemoglobin.*' into a name of 'Hemoglobin'
 *  and a scope of 'Observation' and a matching of 'Partial'.
 *  However this was just never used, so on 2025/01/26 this was removed.
 */
export function fixupFhirNameScopeAndMatching(rule: FhirRuleType): FhirRuleType {
  const { scope, matching, name_blacklist, ...filteredRule } = rule as any; // Exclude deprecated scope and matching
  return { ...filteredRule, type: 'DiagnosticReportName' };
}

/** Take an IBundle of DiagnosticResult and an IBundle of Observation and combines into one array.
 *  Note that if you have bundle with a mix of DiagnosticResult and Observation, just pass it in
 *  as both arguments.  e.g. combineDiagnosticReportsWithObservations(bundle, bundle)
 *  It will filter out any entries that are not DiagnosticResult or Observation from each argument respectively.
 */
export function combineDiagnosticReportsWithObservations(
  rawDiagnosticResult: IDiagnosticReportBundleSimplified,
  rawObservationResult?: IDiagnosticReportBundleSimplified
): IDiagnosticReportSimplified[] | undefined {
  if (!rawDiagnosticResult?.entry || (rawDiagnosticResult.type && rawDiagnosticResult.type !== 'searchset')) {
    return undefined;
  }
  const diagnosticReports = rawDiagnosticResult.entry
    ?.filter((e) => (!e.search?.mode || e.search?.mode === 'match') && e.resource?.resourceType === 'DiagnosticReport')
    .map((e) => e.resource) as IDiagnosticReportSimplified[];
  // console.log('DiagnosticReport:', diagnosticReports);

  // Sort, because the server doesn't seem to do it for us correctly
  diagnosticReports.sort((a, b) => sortByUTCDate(a.effectiveDateTime, b.effectiveDateTime));

  if (!rawObservationResult) return diagnosticReports;

  const observations = rawObservationResult.entry
    ?.filter(
      (e) =>
        (!e.search?.mode || e.search?.mode === 'match' || e.search?.mode === 'include') &&
        e.resource?.resourceType === 'Observation'
    )
    ?.map((e) => e.resource) as IObservationSimplified[];
  const observationsMap = observations.reduce((map, obj) => {
    if (obj.id) {
      map[obj.id] = obj;
    }
    return map;
  }, {} as { [id: string]: IObservationSimplified });
  // console.log('Observations:', observations);
  // diagnosticReports have references to observations:
  // E.g. result[0].reference: "Observation/erUZ6bm58r.rhSBAdZzpan7Kke6aofyo07rdySPjRR0DRsNKD5R1zmPbE4JsVbdPrPTfnsJi4o2k45R.rCyPlMQ3"
  // So we need to replace the reference with the actual observation resource
  diagnosticReports.forEach((dr) => {
    /*if (!dr.result) {
    console.log('dr has no result', dr);
    }*/
    dr.result?.forEach(async (r, index) => {
      if (r.reference) {
        const [t, id] = r.reference.split('/');
        if (t === 'Observation') {
          const obs = observationsMap[id!];
          if (!obs) {
            // const res3 = await pearlApi.get(`/fhir/${r.reference}`);
            // console.log('res3', res3);
            // console.error('Failed to find observation for reference: ', r);
          } else {
            (dr.result![index] as any) = obs;
            delete observationsMap[id!];
          }
        }
      }
    });
  });
  // Push the remaining observations into a new diagnostic report
  diagnosticReports.push({
    resourceType: 'DiagnosticReport',
    code: {},
    id: '',
    result: Object.values(observationsMap) as IReference[],
  });

  // console.log('diagnosticReports with obs is', diagnosticReports);
  return diagnosticReports;
}

export function fixupMRN(mrn: string) {
  // When copying and pasting from epic, the MRN may have a leading 'MRN: '.
  mrn = mrn.trim();
  if (mrn.startsWith('MRN:')) mrn = mrn.slice(4).trim();
  return mrn;
}

export const getRules = (
  predefinedWidgetId: string | undefined, // Parameter,
  predefinedWidgetOptionsMap:
    | {
        [id: string]: FhirPredefinedWidget;
      }
    | undefined
) => getFhirPredefinedWidgetForFhirPageWidget(predefinedWidgetId, predefinedWidgetOptionsMap)?.rules;

export const getFhirPredefinedWidgetForFhirPageWidget = (
  predefinedWidgetId: string | undefined, // Parameter
  predefinedWidgetOptionsMap:
    | {
        [id: string]: FhirPredefinedWidget;
      }
    | undefined
): FhirPredefinedWidget | undefined => {
  const resolvedId = resolveFhirPredefinedWidgetId(predefinedWidgetId, predefinedWidgetOptionsMap);
  return predefinedWidgetOptionsMap?.[resolvedId!];
};

// The original CBC record was removed during consolidation, so it cannot carry its own redirect.
const missingWidgetReplacements: Readonly<Record<string, string>> = {
  'CBC (for anemia)': 'CBC (show diff)',
};

export function resolveFhirPredefinedWidgetId(
  predefinedWidgetId: string | undefined,
  predefinedWidgetOptionsMap: Record<string, FhirPredefinedWidget> | undefined
): string | undefined {
  if (!predefinedWidgetId || !predefinedWidgetOptionsMap) return predefinedWidgetId;

  let resolvedId = predefinedWidgetId;
  const visitedIds = new Set<string>();
  while (!visitedIds.has(resolvedId)) {
    visitedIds.add(resolvedId);
    const widget = predefinedWidgetOptionsMap[resolvedId];
    if (!widget) {
      const replacementId = missingWidgetReplacements[resolvedId];
      if (!replacementId || !predefinedWidgetOptionsMap[replacementId]) return predefinedWidgetId;
      resolvedId = replacementId;
      continue;
    }
    if (!widget.deleted) return resolvedId;
    if (!widget.replacementWidgetId || !predefinedWidgetOptionsMap[widget.replacementWidgetId]) {
      return predefinedWidgetId;
    }
    resolvedId = widget.replacementWidgetId;
  }
  return predefinedWidgetId;
}

/** Get the message where there's no matching result.  @p isPredefinedWidget is whether @p fhir is a predefined widget */
export const noResultFoundMsg = (fhir: FhirWidgetBase | undefined, isPredefinedWidget: boolean) => {
  if (fhir?.msgWhenNoMatches) return fhir?.msgWhenNoMatches;

  return 'No result';
  //if (isPredefinedWidget && (fhir as FhirPredefinedWidget | undefined)?.name) {
  //  return `No result found for ${(fhir as FhirPredefinedWidget)?.name}`;
  //} else {
  //  return 'No result found';
  //}
};

/**
 * When we load a patient, first thing we do is load is a list of all the diagnostic reports.
 * But this doesn't have data about the observations.
 * When one of those diagnostic reports is viewed, we load the diagnostic report again,
 * but including the observations.  Then we pass it to this function to update the patient info.
 */
export function updatePatientDataFromDiagnosticReport(
  fhirPatientData: FhirPatientDataType,
  diagnosticReportWithObserverations: IDiagnosticReportSimplified
) {
  fhirPatientData?.forEach((diagnosticReport) => {
    if (diagnosticReport.id === diagnosticReportWithObserverations.id) {
      diagnosticReport.result = diagnosticReportWithObserverations.result;
    }
  });
}

/* Our rules match normalized CPT codes and report names,
 * so it's a string like   12345|12346   or  1234[5-6]
 * but nothing more complicated than that.
 *
 * But for debugging etc, the rule could be .*
 * In this case we set needToFetchAll to true.
 */
export function convertFhirRulesToListOfCPTCodes(rules: FhirRuleType | undefined) {
  const cptList = [] as string[];
  const nameList = [] as string[];
  rules?.name.split('|').forEach((cpt) => {
    if (!cpt) return;
    // The cpt code can be a number like 12345 in which case we just return that,
    // but it can also be a range like 1234[0-5] which we need to expand to
    // [12340, 12341, 12342, 12343, 12344, 12345]
    if (cpt.length === 5 && cpt.match(/^[0-9A-Za-z]+$/)) {
      cptList.push(cpt);
      return;
    }
    if (cpt.length === 9) {
      // Check for "1234[0-5]" which is 9 characters long
      const match = cpt.match(/^([0-9A-Za-z]+)\[([0-9])-([0-9])\]$/);
      if (match) {
        const prefix = match[1]!;
        const start = parseInt(match[2]!);
        const end = parseInt(match[3]!);
        for (let i = start; i <= end; i++) {
          cptList.push(prefix + i);
        }
        return;
      }
    }
    // Treat it as a name instead
    nameList.push(cpt);
  });
  // FIXME: Currently we don't support nameList because the problem is that we have stripped out
  // special characters from the name, so we can't match it back to the original name.
  return { cptList, nameList };
}

export function escapeFhir(str: string) {
  str = str.replaceAll('&', '%26'); // First, replace any "&" with "%26"
  str = str.replace(/[?]/g, '%3F'); // Then replace any "?" with "%3F"
  // Then add a slash before any of: [$,|]
  str = str.replace(/([$,|])/g, '\\$1');
  return str;
}

/** Red flag cpt codes that are ambigious because the same code has been used for different labs.
 *
 * See ticket: PRL-5497 for trying to move this to the server.
 */
const problematicCPTCodesSet = new Set([
  '36415',
  '36416',
  '46415',
  '70496',
  '72020',
  '73502',
  '73721',
  '74178',
  '76705',
  '80047',
  '80048',
  '80053',
  '80061',
  '80069',
  '80074',
  '80101',
  '80104',
  '80299',
  '80300',
  '80301',
  '80305',
  '80306',
  '80307',
  '80321',
  '80329',
  '80346',
  '80703',
  '81001',
  '81002',
  '81003',
  '81015',
  '81050',
  '81162',
  '81377',
  '81381',
  '82003',
  '82010',
  '82040',
  '82042',
  '82088',
  '82103',
  '82150',
  '82164',
  '82172',
  '82247',
  '82248',
  '82310',
  '82330',
  '82340',
  '82374',
  '82378',
  '82384',
  '82397',
  '82435',
  '82507',
  '82530',
  '82533',
  '82542',
  '82550',
  '82565',
  '82570',
  '82575',
  '82607',
  '82728',
  '82746',
  '82784',
  '82785',
  '82803',
  '82805',
  '82834',
  '82835',
  '82945',
  '82947',
  '82950',
  '82977',
  '83002',
  '83020',
  '83021',
  '83036',
  '83068',
  '83090',
  '83516',
  '83519',
  '83520',
  '83521',
  '83550',
  '83655',
  '83721',
  '83735',
  '83835',
  '83874',
  '83883',
  '83925',
  '83945',
  '83970',
  '83986',
  '84075',
  '84100',
  '84132',
  '84153',
  '84154',
  '84155',
  '84156',
  '84157',
  '84166',
  '84244',
  '84270',
  '84295',
  '84305',
  '84402',
  '84403',
  '84443',
  '84450',
  '84460',
  '84466',
  '84520',
  '84550',
  '84586',
  '84591',
  '84999',
  '85013',
  '85014',
  '85018',
  '85025',
  '85049',
  '85060',
  '85210',
  '85240',
  '85245',
  '85246',
  '85320',
  '85335',
  '85370',
  '85379',
  '85384',
  '85420',
  '85576',
  '85598',
  '85610',
  '85613',
  '85651',
  '85652',
  '85670',
  '85722',
  '85729',
  '85730',
  '85732',
  '86003',
  '86038',
  '86140',
  '86146',
  '86147',
  '86148',
  '86160',
  '86215',
  '86225',
  '86235',
  '86255',
  '86256',
  '86258',
  '86316',
  '86317',
  '86334',
  '86336',
  '86337',
  '86341',
  '86364',
  '86376',
  '86381',
  '86592',
  '86593',
  '86635',
  '86658',
  '86682',
  '86695',
  '86696',
  '86705',
  '86709',
  '86735',
  '86765',
  '86780',
  '86790',
  '86800',
  '86803',
  '86850',
  '86900',
  '87015',
  '87025',
  '87035',
  '87045',
  '87070',
  '87077',
  '87081',
  '87086',
  '87102',
  '87109',
  '87205',
  '87206',
  '87209',
  '87210',
  '87254',
  '87287',
  '87328',
  '87329',
  '87340',
  '87449',
  '87454',
  '87476',
  '87486',
  '87491',
  '87505',
  '87517',
  '87529',
  '87541',
  '87591',
  '87621',
  '87624',
  '87631',
  '87633',
  '87661',
  '87798',
  '87799',
  '87801',
  '87810',
  '87880',
  '87899',
  '87902',
  '88112',
  '88175',
  '88300',
  '88305',
  '88403',
  '89051',
  '93306',
  '93925',
]);

export function isProblematicCPTCode(cptCode: string) {
  return problematicCPTCodesSet.has(cptCode);
}

/** Unresolved DiagnosticReport.result entries are References, which have no resourceType. */
export function isObservationFetched(o?: IObservation): o is IObservation {
  return o?.resourceType === 'Observation' || !!o?.code?.text;
}

/** Look in the predefined widgets for CPTs listed in the hideAllIfNoWhitelistMatches
 *  and are not already in problematicCPTCodes.  This is a heuristic to guess new
 *  problematic CPT codes to add.
 */
export function guessNewProblematicCPTCodes(predefinedWidgets: FhirWidgetBase[]) {
  // CPT codes are 5 digits long, so test for that
  const cptRegex = /^[0-9]{5}$/;
  const ret = new Set<String>();
  predefinedWidgets?.forEach((x) => {
    const rule = x?.rules;
    if (rule?.type === 'DiagnosticReportName') {
      rule.hideAllIfNoWhitelistMatches
        ?.split('|')
        ?.filter((x) => cptRegex.test(x) && !problematicCPTCodesSet.has(x))
        ?.forEach((x) => ret.add(x));
    }
  });
  return Array.from(ret).sort();
}

export function monthDiff(dateFrom: Date, dateTo: Date = new Date()) {
  return dateTo.getMonth() - dateFrom.getMonth() + 12 * (dateTo.getFullYear() - dateFrom.getFullYear());
}

export const defaultPageFhir: PageFhirType = {
  idsToFhir: {},
};
export const defaultFhir: Omit<FhirPageWidgetType, 'id'> = {
  predefinedWidgets: [],
};

const NORMAL_INTERPRETATION_CODES = new Set(['N', 'NEG', 'ND', 'NR']);

/** Map an Observation.interpretation code to our enum.
 * See https://terminology.hl7.org/5.1.0/CodeSystem-v3-ObservationInterpretation.html
 */
export function getInterpretationFromCode(code: string | undefined): ObservationValueInterpretationEnum | undefined {
  if (!code) return undefined;
  const upperCode = code.toUpperCase();
  if (NORMAL_INTERPRETATION_CODES.has(upperCode)) return ObservationValueInterpretationEnum.NORMAL;
  if (upperCode[0] === 'H') return ObservationValueInterpretationEnum.HIGH;
  if (upperCode[0] === 'L') return ObservationValueInterpretationEnum.LOW;
  return ObservationValueInterpretationEnum.ABNORMAL;
}

/** Interpret a value against a text-only reference range such as "<200", "< 5.7 %" or ">= 90 mL/min". */
export function getTextReferenceRangeInterpretation(
  text: string,
  value: number
): ObservationValueInterpretationEnum | undefined {
  const m = text.trim().match(/^(<=|>=|≤|≥|<|>)\s*(\d+(?:\.\d+)?)(?![\d.])/);
  if (!m) return undefined;
  const limit = Number(m[2]);
  const { NORMAL, HIGH, LOW } = ObservationValueInterpretationEnum;
  switch (m[1]) {
    case '<':
      return value < limit ? NORMAL : HIGH;
    case '<=':
    case '≤':
      return value <= limit ? NORMAL : HIGH;
    case '>':
      return value > limit ? NORMAL : LOW;
    default:
      return value >= limit ? NORMAL : LOW;
  }
}

/** This is JSX free version of `getObservationValue` method of `FhirWidget`. It can be used in FhirWidget as well later.
 *
 *  Return a 2-tuple with the first value being the observation value as a string,
 * and the second being an the value is above or below the reference range.
 */
export function getObservationValueWithInterpretation({
  observation,
}: {
  observation: IObservationSimplified | undefined;
}): [string | null, ObservationValueInterpretationEnum | null] {
  function getObservationValueAndInterpretation(): [string | null, ObservationValueInterpretationEnum | null] {
    if (!observation) return [null, null];
    if (observation.valueQuantity) {
      const value = observation.valueQuantity?.value;
      if (value == null) {
        return [null, null];
      }
      const valueStr = `${observation.valueQuantity?.comparator || ''}${value}`;
      let foundRange = false;
      for (let i = 0; observation.referenceRange && i < observation.referenceRange?.length; i++) {
        const range = observation.referenceRange[i];
        if (!range?.low && !range?.high && range?.text) {
          const textInterpretation = getTextReferenceRangeInterpretation(range.text, value);
          if (textInterpretation) {
            foundRange = true;
            if (textInterpretation !== ObservationValueInterpretationEnum.NORMAL) {
              return [valueStr, textInterpretation];
            }
          }
        }
        if (range?.low?.value != null) {
          foundRange = true;
          if (value < range?.low?.value) {
            return [valueStr, ObservationValueInterpretationEnum.LOW];
          }
        }
        if (range?.high?.value != null) {
          foundRange = true;
          if (value > range?.high?.value!) {
            return [valueStr, ObservationValueInterpretationEnum.HIGH];
          }
        }
      }
      if (foundRange) {
        return [valueStr, ObservationValueInterpretationEnum.NORMAL];
      }
      return [valueStr, ObservationValueInterpretationEnum.NONE];
    } else if (observation.valueCodeableConcept?.text) {
      const snomed = observation.valueCodeableConcept!.coding?.find((c) => c.system === 'http://snomed.info/sct');
      if (snomed?.code) {
        return [`SNOMED Code: ${observation.valueCodeableConcept?.text}`, ObservationValueInterpretationEnum.NONE];
      } else {
        return [`${observation.valueCodeableConcept?.text}`, ObservationValueInterpretationEnum.NONE];
      }
    }
    if (observation.valueString) {
      return [`${observation.valueString}`, ObservationValueInterpretationEnum.NONE];
    }
    if (observation.valueBoolean) {
      return [observation.valueBoolean ? 'positive' : 'negative', ObservationValueInterpretationEnum.NONE];
    }
    if (observation.valueDateTime) {
      return [`${formatDateTime(observation.valueDateTime)}`, ObservationValueInterpretationEnum.NONE];
    }
    if (observation.valueRatio) {
      const ratio = observation.valueRatio;
      return [
        `Ratio: ${ratio.numerator?.comparator}${ratio.numerator?.value}:${ratio.denominator?.value}${ratio.numerator?.unit}`,
        ObservationValueInterpretationEnum.NONE,
      ];
    }
    if (observation.dataAbsentReason?.text && !observation.note) {
      return [`Data absent reason: ${observation.dataAbsentReason!.text}`, ObservationValueInterpretationEnum.NONE];
    }
    return [null, ObservationValueInterpretationEnum.NONE];
  }
  function getInterpretation(
    initialInterpretation: ObservationValueInterpretationEnum | null
  ): ObservationValueInterpretationEnum {
    return (
      getInterpretationFromCode(observation?.interpretation?.[0]?.coding?.[0]?.code) ??
      initialInterpretation ??
      ObservationValueInterpretationEnum.NONE
    );
  }
  let [observationValue, initialInterpretation] = getObservationValueAndInterpretation();
  let interpretation = getInterpretation(initialInterpretation);

  return [observationValue, interpretation];
}

// Get the widgets by the document version
export function getWidgetsFromDocumentVersion(documentVersion: DocumentVersionType | undefined): FhirPageWidgetType[] {
  const widgets: FhirPageWidgetType[] = [];
  if (documentVersion) {
    for (const pageId in documentVersion.pages) {
      const page = documentVersion.pages[pageId];
      const content = page?.content;
      const pageFhir = page?.fhir || defaultPageFhir;
      for (const id in pageFhir.idsToFhir) {
        const fhir = pageFhir.idsToFhir[id] || { ...defaultFhir, id };
        // Only pull the widgets which are being used
        if (content && content.includes(id)) widgets.push(fhir);
      }
    }
  }
  return widgets;
}

// Converts the digits coming from the FHIR API to superscript
export function toSuperscript(d: string) {
  if (d === '2') return 'x10²';
  if (d === '3') return 'x10³';
  if (d === '4') return 'x10⁴';
  if (d === '5') return 'x10⁵';
  if (d === '6') return 'x10⁶';
  if (d === '7') return 'x10⁷';
  if (d === '8') return 'x10⁸';
  if (d === '9') return 'x10⁹';
  if (d === '10') return 'x10¹⁰';
  if (d === '11') return 'x10¹¹';
  if (d === '12') return 'x10¹²';
  return `^${d}`;
}

export const fhirDateFilterValues = [
  { key: 1, value: 'Last 24 hours' },
  { key: 30, value: 'Last 1 month' },
  { key: 90, value: 'Last 3 months' },
  { key: 180, value: 'Last 6 months' },
  { key: 365, value: 'Last year' },
  { key: 0, value: 'All' },
];

export const contextualizationTags = ['contextualized', 'contextualized_forced'];

export const fhirDateRangeValuesMap = fhirDateFilterValues.reduce((map, obj) => {
  map[obj.key] = obj.value;
  return map;
}, {} as { [key: number]: string });

export function isObservationsWithInDateRange(date: string | undefined, dateRange: number): boolean {
  if (date && dateRange) {
    const givenDate = new Date(date);
    const currentDate = new Date();
    const pastDate = new Date(new Date().getTime() - dateRange * 24 * 60 * 60 * 1000);

    // Check if the observation date is within the selected date range
    return givenDate >= pastDate && givenDate <= currentDate;
  }
  return false;
}

// cleanText fn. remove text inside parentheses, including the parentheses,
// trim whitespaces and transform to lowercase for getSuggestedWidgets
function cleanText(text: string = ''): string {
  return text
    .replace(/\([^)]*\)/g, '')
    .trim()
    .toLowerCase();
}

function escapedRegExp(text: string): RegExp {
  const excapedText = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${excapedText}\\b`);
}

export function getManualContextualizationSearchTerms(
  widget: Pick<FhirPredefinedWidget, 'searchTerms' | 'manualSearchTerms'>
) {
  return [widget.searchTerms, widget.manualSearchTerms].filter(Boolean).join(', ');
}

export function getSuggestedWidgets(
  contextualizedText: string | undefined,
  predefinedWidgetsNotDeleted: FhirPredefinedWidget[],
  acronyms: Record<string, string> | undefined
) {
  try {
    if (!contextualizedText) return [];
    const bpPattern = /\b(bp|blood pressure)\b/i;

    if (bpPattern.test(contextualizedText)) {
      return [];
    }
    const contextualizedTextLowerCased = contextualizedText.trim().toLowerCase();
    const contextualizedTextRegex = escapedRegExp(contextualizedTextLowerCased);
    const expandedAcronymLowerCased = (acronyms?.[contextualizedText] || '').trim().toLowerCase();
    const expandedAcronymRegex = escapedRegExp(expandedAcronymLowerCased);

    // Do a fuzzy search for the best match
    return (
      predefinedWidgetsNotDeleted
        .map((widget) => {
          const isNegativeMatch = (widget.negativeSearchTerms || '')
            .split(',')
            .map(cleanText)
            .filter(Boolean)
            .some((negativeKeyword) => escapedRegExp(negativeKeyword).test(contextualizedTextLowerCased));
          if (isNegativeMatch) return undefined;

          const widgetNameLowerCased = cleanText(widget.name);
          const keywordsLowerCased = getManualContextualizationSearchTerms(widget).toLowerCase();
          const keywordListLowerCased = keywordsLowerCased?.split(',').map(cleanText) || [];
          const whitelistLowerCased = widget.whitelist?.map((w) => cleanText(w.name)) || [];
          const whitelistStringLowerCased = whitelistLowerCased.join(',');

          // prioritization of matches
          if (widgetNameLowerCased === contextualizedTextLowerCased) return [30, widget] as const;
          if (widgetNameLowerCased === expandedAcronymLowerCased) return [28, widget] as const;
          if (widgetNameLowerCased.startsWith(contextualizedTextLowerCased)) return [26, widget] as const;
          if (expandedAcronymLowerCased && widgetNameLowerCased.startsWith(expandedAcronymLowerCased))
            return [24, widget] as const;
          if (keywordListLowerCased.includes(contextualizedTextLowerCased)) return [22, widget] as const;
          if (expandedAcronymLowerCased && keywordListLowerCased.includes(expandedAcronymLowerCased))
            return [20, widget] as const;
          if (expandedAcronymLowerCased && whitelistLowerCased.includes(expandedAcronymLowerCased))
            return [18, widget] as const;
          if (whitelistLowerCased.includes(contextualizedTextLowerCased)) return [16, widget] as const;
          if (expandedAcronymLowerCased && expandedAcronymRegex.test(widgetNameLowerCased))
            return [14, widget] as const;
          if (contextualizedTextRegex.test(widgetNameLowerCased)) return [12, widget] as const;
          if (expandedAcronymLowerCased && expandedAcronymRegex.test(keywordsLowerCased)) return [10, widget] as const;
          if (contextualizedTextRegex.test(keywordsLowerCased)) return [8, widget] as const;
          if (expandedAcronymLowerCased && expandedAcronymRegex.test(whitelistStringLowerCased))
            return [6, widget] as const;
          if (contextualizedTextRegex.test(whitelistStringLowerCased)) return [4, widget] as const;

          return undefined;
        })
        // remove undefined values
        .filter(
          (x): x is readonly [30 | 28 | 26 | 24 | 22 | 20 | 18 | 16 | 14 | 12 | 10 | 8 | 6 | 4, FhirPredefinedWidget] =>
            !!x
        )
        // sort by priority & name
        .sort((a, b) => b[0] - a[0] || a[1].name.localeCompare(b[1].name))
        // limit to first 10
        .slice(0, 10)
    );
  } catch (e) {
    console.log(`failed to find widget for ${contextualizedText}`, e);
    return [];
  }
}

export function forEachOptionOfPage(
  page: PageType | undefined,
  cb: (option: PageOptionExclusiveType | PageOptionMultipleChoiceType) => void
) {
  if (page?.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page?.pageType === PageTypeEnum.CALCULATOR) {
    page.questions?.forEach((question) => {
      if (question.type === 'multiple_choice' || question.type === 'exclusive')
        question.options?.map((option) => cb(option));
    });
  }
}

export function fhirObservationValueToText(e: IObservationSimplified): string {
  if (e.valueQuantity && e.valueQuantity.unit === '%') {
    return `${e.valueQuantity.value || ''}%`;
  }
  return (
    (e.valueQuantity && `${e.valueQuantity!.value || ''} ${e.valueQuantity!.unit || ''}`) ||
    e.valueCodeableConcept?.text ||
    e.valueString ||
    (e.valueBoolean !== undefined && e.valueBoolean.toString()) ||
    (e.valueRatio && `${e.valueRatio!.numerator?.value}/${e.valueRatio!.denominator?.value}`) ||
    e.valueDateTime ||
    e.dataAbsentReason?.text ||
    ''
  );
}

/**
 * Create a unique ID for a FHIR widget in the right panel.
 * If we are viewing this when editing a guide with ckeditor, then we aren't collapsing identical widgets
 * into one, so we need to include the fhirWidgetId in the ID to make it unique. A <fhir> widget can
 * have multiple widgets, so the fhirWidgetId is not unique by itself. We need both the predefinedWidgetId
 * and the fhirWidgetId to make it unique.
 */
export function fhirWidgetIdToRightPanelWidgetId(
  fhirWidgetId: string,
  predefinedWidgetId: string | undefined,
  messageIndex: number,
  inEditorMode: boolean /* If we are viewing this when editing a guide with ckeditor */
) {
  return `fw${Math.floor(messageIndex)}-${predefinedWidgetId || fhirWidgetId}${
    inEditorMode && predefinedWidgetId ? `-${fhirWidgetId}` : ''
  }`;
}
